import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxT2v01Params {
  /**
   * 视频生成提示词，必填，长度必须在1-2000个字符之间
   */
  prompt: string;
  /**
   * 视频时长（秒），T2V-01模型固定为6秒，不可更改
   * @default 6
   * @values 6
   */
  duration?: 6;
  /**
   * 视频分辨率，T2V-01模型固定为720P，不可更改
   * @default "720P"
   * @values 720P
   */
  resolution?: '720P';
  /**
   * 是否启用提示词优化器，可选，默认为true
   * @default true
   */
  prompt_optimizer?: boolean;
}

export interface MinimaxT2v01Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_t2v_01
 *
 * MiniMax T2V-01 文本到视频生成API
 * 
 * Model: T2V-01
 * Features: 基础视频生成功能
 * Duration: 6秒（固定参数，不可更改）
 * Resolution: 720P（固定参数，不可更改）
 * Prompt Optimizer: 支持提示词优化，默认启用
 * 
 * API Documentation: https://www.minimax.io/platform/document/text_to_video?key=68abdfffd08627aad9674bff
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxT2v01 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxT2v01({
 *   // 参数见 MinimaxT2v01Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxT2v01Params 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxT2v01(
  params: MinimaxT2v01Params,
): Promise<MinimaxT2v01Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_t2v_01',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxT2v01Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
